import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Edit, Download, Send, Mail, MessageCircle, CheckCircle, XCircle, FileText, Clock, User, MapPin, Calendar, Package, Phone, Building, CreditCard, IndianRupee, ArrowRight, ShoppingBag, Loader2 } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import WorkflowTimeline from '../../components/shared/WorkflowTimeline';
import ConfirmModal from '../../components/modals/ConfirmModal';
import api from '../../services/api';
import { quotationsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const QuotationDetailPage = () => {
  const { businessId, quotationId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid && quotationId) {
      fetchQuotation();
    }
  }, [bid, quotationId]);

  const fetchQuotation = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/quotations/${quotationId}`);
      setQuotation(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load quotation'));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsSent = () => {
    setConfirmModal({
      title: 'Mark as Sent',
      description: 'Mark this quotation as sent to the customer?',
      confirmLabel: 'Mark as Sent',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: Send,
      iconClass: 'text-blue-600',
      iconBgClass: 'bg-blue-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.put(`/quotations/${quotationId}`, { status: 'SENT' });
          toast.success('Marked as sent');
          fetchQuotation();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to update status'));
        }
      },
    });
  };

  const handleApprove = () => {
    setConfirmModal({
      title: 'Mark as Approved',
      description: 'Record that the customer has approved this quotation?',
      confirmLabel: 'Mark Approved',
      confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
      icon: CheckCircle,
      iconClass: 'text-emerald-600',
      iconBgClass: 'bg-emerald-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.put(`/quotations/${quotationId}`, { status: 'APPROVED' });
          toast.success('Quotation approved');
          fetchQuotation();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to approve quotation'));
        }
      },
    });
  };

  // Rejection uses the children slot of ConfirmModal so the reason
  // textarea is captured inside the same confirmation flow (was
  // window.prompt before). Reason persists into meta.rejection_reason
  // on the PUT, matching the prior behaviour.
  const handleReject = () => {
    setRejectReason('');
    setConfirmModal({
      title: 'Reject Quotation',
      description: 'Record the customer\u2019s reason for rejecting so your team has context later.',
      confirmLabel: 'Reject',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      kind: 'reject',
      onConfirm: async (reason) => {
        setConfirmModal(null);
        try {
          await api.put(`/quotations/${quotationId}`, {
            status: 'REJECTED',
            meta: { ...(quotation?.meta || {}), rejection_reason: reason || '' },
          });
          toast.success('Quotation rejected');
          fetchQuotation();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to reject quotation'));
        }
      },
    });
  };

  const handleConvertToSalesOrder = () => {
    setConfirmModal({
      title: 'Convert to Sales Order',
      description: 'A new Sales Order will be created from this quotation\u2019s items and totals.',
      confirmLabel: 'Convert',
      confirmClass: 'bg-purple-600 hover:bg-purple-700',
      icon: ShoppingBag,
      iconClass: 'text-purple-600',
      iconBgClass: 'bg-purple-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await quotationsAPI.convertToSalesOrder(quotationId);
          const result = response.data || {};
          const newOrder = result.sales_order || {};
          toast.success(`Converted to Sales Order ${newOrder.order_number || ''}`);
          if (newOrder.id) {
            navigate(`/business/${bid}/sales-orders/${newOrder.id}`);
          } else {
            fetchQuotation();
          }
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to convert quotation to sales order'));
        }
      },
    });
  };

  const handleConvertToInvoice = () => {
    setConfirmModal({
      title: 'Convert to Invoice',
      description: 'A new Sales Invoice (DRAFT) will be created from this quotation.',
      confirmLabel: 'Convert',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: ArrowRight,
      iconClass: 'text-blue-600',
      iconBgClass: 'bg-blue-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await quotationsAPI.convertToInvoice(quotationId);
          const invoice = response.data || {};
          toast.success(`Converted to invoice ${invoice.doc_no || ''}`);
          if (invoice.id) {
            navigate(`/business/${bid}/sales/invoices/${invoice.id}`);
          } else {
            fetchQuotation();
          }
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to convert to invoice'));
        }
      },
    });
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await quotationsAPI.downloadPdf(quotationId);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Quotation_${quotation?.doc_no || quotationId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  // Stub endpoints write to documents.activity_log and return
  // {success, message, note}. Surface the note so the user knows the
  // action was logged but delivery is pending. Same pattern as
  // SalesOrderDetailPage.
  const handleSendEmail = async () => {
    setSendingEmail(true);
    try {
      const response = await quotationsAPI.sendEmail(quotationId);
      const data = response.data || {};
      toast.success(data.message || 'Email queued');
      if (data.note) {
        toast(data.note, { icon: '\u2139\ufe0f', duration: 4000 });
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to queue email'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendWhatsApp = async () => {
    try {
      const response = await quotationsAPI.sendWhatsApp(quotationId);
      const data = response.data || {};
      toast.success(data.message || 'WhatsApp message queued');
      if (data.note) {
        toast(data.note, { icon: '\u2139\ufe0f', duration: 4000 });
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to queue WhatsApp'));
    }
  };

  // Smooth navigation with transition
  const handleBack = () => {
    navigate(`/business/${bid}/sales/quotations`);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading quotation details...</p>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-[16px] text-slate-600 font-medium">Quotation not found</p>
          <button onClick={handleBack} className="mt-4 text-blue-600 hover:underline text-[14px]">
            ← Back to Quotations
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = {
    DRAFT: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock, label: 'Draft' },
    SENT: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Send, label: 'Sent' },
    APPROVED: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle, label: 'Approved' },
    REJECTED: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Rejected' },
    EXPIRED: { bg: 'bg-slate-100', text: 'text-slate-700', icon: Clock, label: 'Expired' },
    CONVERTED: { bg: 'bg-purple-100', text: 'text-purple-700', icon: ArrowRight, label: 'Converted' },
  };

  const statusBadge = statusConfig[quotation.status] || statusConfig.DRAFT;
  const StatusIcon = statusBadge.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Extract customer/party info
  const customerName = quotation.party?.name || quotation.party_name || quotation.customer_name || '';
  const customerPhone = quotation.party?.phone || quotation.party?.mobile || '';
  const customerEmail = quotation.party?.email || '';
  const customerGstin = quotation.party?.gstin || '';
  const billingAddress = quotation.party?.billing_address || quotation.party?.address || '';

  // Items - support both formats
  const items = quotation.items || quotation.line_items || [];

  // Reject flow uses the ConfirmModal children slot for the reason textarea.
  // Track it outside the modal config so the confirm handler sees the latest
  // value (config is captured by reference when the modal opens).
  const isRejectFlow = confirmModal?.kind === 'reject';

  return (
    <>
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* ════════════════════════════════════════════════════════════════════════
          PREMIUM HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back + Quotation Info */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[20px] font-bold text-slate-800">{quotation.doc_no || quotation.quotation_no}</h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {statusBadge.label}
                  </span>
                </div>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  Created on {formatDate(quotation.audit?.created_at || quotation.created_at)} • {items.length || 0} item{items.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              {quotation.status === 'DRAFT' && (
                <>
                  <button
                    onClick={() => navigate(`/business/${bid}/sales/quotations/${quotationId}/edit`)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={handleMarkAsSent}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-blue-600/20 active:scale-[0.98]"
                  >
                    <Send className="w-4 h-4" />
                    Mark as Sent
                  </button>
                </>
              )}
              {quotation.status === 'SENT' && (
                <>
                  <button
                    onClick={handleApprove}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-emerald-600/20 active:scale-[0.98]"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Mark Approved
                  </button>
                  <button
                    onClick={handleReject}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                </>
              )}
              {quotation.status === 'APPROVED' && (
                <>
                  <button
                    onClick={handleConvertToSalesOrder}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-purple-600/20 active:scale-[0.98]"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Convert to Sales Order
                  </button>
                  <button
                    onClick={handleConvertToInvoice}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-blue-600/20 active:scale-[0.98]"
                  >
                    <FileText className="w-4 h-4" />
                    Convert to Invoice
                  </button>
                </>
              )}
              <div className="w-px h-8 bg-slate-200 mx-1"></div>
              <button
                onClick={handleDownloadPdf}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]"
                title="Download PDF"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail}
                className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Send Email"
              >
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </button>
              <button
                onClick={handleSendWhatsApp}
                className="inline-flex items-center justify-center w-10 h-10 text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all duration-200 active:scale-[0.98]"
                title="Send WhatsApp"
              >
                <MessageCircle className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          MAIN CONTENT - Premium 3-Column Layout
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - Customer & Quotation Details (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Customer Card - Premium */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Customer</h2>
                </div>
                
                {customerName ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-[16px] font-bold text-blue-700">
                          {customerName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{customerName}</p>
                        <p className="text-[12px] text-slate-500">Customer</p>
                      </div>
                    </div>
                    
                    {customerPhone && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {customerPhone}
                      </div>
                    )}
                    
                    {customerEmail && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {customerEmail}
                      </div>
                    )}
                    
                    {customerGstin && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Building className="w-4 h-4 text-slate-400" />
                        GSTIN: {customerGstin}
                      </div>
                    )}

                    {billingAddress && (
                      <div className="flex items-start gap-2 text-[13px] text-slate-600">
                        <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                        <span>{typeof billingAddress === 'object' ? `${billingAddress.line1 || ''} ${billingAddress.city || ''} ${billingAddress.state || ''}` : billingAddress}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-2">
                      <User className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-[13px] text-slate-500">No customer selected</p>
                  </div>
                )}
              </div>

              {/* Validity Card - Premium */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                    <Calendar className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Validity Details</h2>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Quotation Date</p>
                    <p className="text-[14px] font-medium text-slate-700">
                      {formatDate(quotation.doc_date)}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Valid Until</p>
                    <p className="text-[14px] font-medium text-slate-700">
                      {quotation.meta?.valid_until ? formatDate(quotation.meta.valid_until) : '—'}
                    </p>
                  </div>
                  
                  {quotation.meta?.reference_no && (
                    <div>
                      <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Reference</p>
                      <p className="text-[14px] font-medium text-slate-700">{quotation.meta.reference_no}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Quotation Info Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Quotation Info</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Status</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                      {statusBadge.label}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Place of Supply</span>
                    <span className="text-[13px] font-medium text-slate-700">{quotation.meta?.place_of_supply || '—'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Currency</span>
                    <span className="text-[13px] font-medium text-slate-700">{quotation.currency || 'INR'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CENTER COLUMN - Items Table (6 cols) */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                {/* Items Header */}
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <Package className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Quotation Items</h2>
                      <p className="text-[12px] text-slate-500">{items.length || 0} item{items.length !== 1 ? 's' : ''} in this quotation</p>
                    </div>
                  </div>
                </div>
                
                {/* Items Table */}
                {items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">#</th>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Item Details</th>
                          <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Qty</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Rate</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Tax</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50/50 transition-colors duration-150">
                            <td className="px-5 py-4 text-[13px] text-slate-400 font-medium">{index + 1}</td>
                            <td className="px-5 py-4">
                              <p className="text-[14px] font-medium text-slate-800">{item.name || item.item_name}</p>
                              {item.hsn_sac && (
                                <p className="text-[11px] text-slate-400 mt-0.5">HSN: {item.hsn_sac}</p>
                              )}
                              {item.description && (
                                <p className="text-[11px] text-slate-400 mt-0.5">{item.description}</p>
                              )}
                            </td>
                            <td className="px-5 py-4 text-center">
                              <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 rounded-lg text-[13px] font-medium text-slate-700">
                                {item.qty || item.quantity} {item.unit || ''}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-right text-[13px] text-slate-600">
                              ₹{(item.price || item.rate || item.unit_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-5 py-4 text-right text-[13px] text-slate-600">
                              {item.tax_rate > 0 ? `${item.tax_rate}%` : '—'}
                            </td>
                            <td className="px-5 py-4 text-right">
                              <span className="text-[14px] font-semibold text-emerald-600">
                                ₹{(item.line_total || item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-[14px] text-slate-500">No items in this quotation</p>
                  </div>
                )}

                {/* Notes Section */}
                {quotation.terms?.notes_public && (
                  <div className="px-5 py-4 bg-amber-50 border-t border-amber-100">
                    <p className="text-[11px] text-amber-600 uppercase tracking-wide font-semibold mb-1">Customer Notes</p>
                    <p className="text-[13px] text-amber-800">{quotation.terms.notes_public}</p>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN - Summary (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Summary Card - Premium Green */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-5 shadow-sm border border-emerald-200/60">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <IndianRupee className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Quotation Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">₹{(quotation.totals?.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  {(quotation.totals?.discount_total || 0) > 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-600">Discount</span>
                      <span className="text-red-500 font-medium">-₹{(quotation.totals.discount_total).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-slate-800">₹{(quotation.totals?.tax_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  {(quotation.totals?.round_off || 0) !== 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-600">Round Off</span>
                      <span className="text-slate-800">₹{(quotation.totals.round_off).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  
                  <div className="border-t border-emerald-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Grand Total</span>
                      <span className="text-[22px] font-bold text-emerald-600">
                        ₹{(quotation.totals?.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tax Breakup Card */}
              {quotation.tax_breakup && quotation.tax_breakup.length > 0 && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                      <CreditCard className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Tax Breakup</h2>
                  </div>
                  
                  <div className="space-y-3">
                    {quotation.tax_breakup.map((tax, idx) => (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-[12px] text-slate-500">GST @ {tax.tax_rate}%</span>
                        <span className="text-[13px] font-medium text-slate-800">
                          ₹{(tax.tax || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <h2 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  <button
                    onClick={handleDownloadPdf}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors duration-200 text-left"
                  >
                    <Download className="w-5 h-5 text-slate-500" />
                    <span className="text-[13px] font-medium text-slate-700">Download PDF</span>
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={sendingEmail}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingEmail ? <Loader2 className="w-5 h-5 text-slate-500 animate-spin" /> : <Mail className="w-5 h-5 text-slate-500" />}
                    <span className="text-[13px] font-medium text-slate-700">{sendingEmail ? 'Sending...' : 'Send via Email'}</span>
                  </button>
                  <button
                    onClick={handleSendWhatsApp}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors duration-200 text-left"
                  >
                    <MessageCircle className="w-5 h-5 text-emerald-600" />
                    <span className="text-[13px] font-medium text-emerald-700">Send via WhatsApp</span>
                  </button>
                </div>
              </div>

              {/* Workflow Timeline */}
              <WorkflowTimeline
                currentDocument={{ type: 'QUOTATION', id: quotationId, number: quotation.doc_no }}
                linkedDocuments={{
                  quotation: { id: quotationId, number: quotation.doc_no },
                  salesOrder: quotation.converted_sales_order_id ? {
                    id: quotation.converted_sales_order_id,
                    number: quotation.converted_sales_order_number
                  } : null,
                  invoice: quotation.converted_invoice_id ? {
                    id: quotation.converted_invoice_id,
                    number: quotation.converted_invoice_number
                  } : null
                }}
                businessId={bid}
              />

              {/* Terms & Conditions */}
              {quotation.terms?.terms_lines && quotation.terms.terms_lines.length > 0 && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                  <h2 className="text-[14px] font-semibold text-slate-800 mb-3">Terms & Conditions</h2>
                  <ul className="space-y-2">
                    {quotation.terms.terms_lines.map((term, idx) => (
                      <li key={idx} className="text-[12px] text-slate-600 flex items-start gap-2">
                        <span className="text-slate-400 mt-0.5">•</span>
                        {term}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    <ConfirmModal
      isOpen={!!confirmModal}
      title={confirmModal?.title}
      description={confirmModal?.description}
      confirmLabel={confirmModal?.confirmLabel}
      confirmClass={confirmModal?.confirmClass}
      icon={confirmModal?.icon}
      iconClass={confirmModal?.iconClass}
      iconBgClass={confirmModal?.iconBgClass}
      // For reject flow, disable the confirm button until a reason is provided
      // so sales teams get a non-empty record of why deals are dying.
      confirmDisabled={isRejectFlow && !rejectReason.trim()}
      onConfirm={() => confirmModal?.onConfirm?.(rejectReason)}
      onClose={() => { setConfirmModal(null); setRejectReason(''); }}
    >
      {isRejectFlow && (
        <textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. Price too high, went with another vendor, timeline too tight..."
          rows={3}
          autoFocus
          className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 focus:outline-none resize-none"
        />
      )}
    </ConfirmModal>
    </>
  );
};

export default QuotationDetailPage;
